import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./rendimentiPerAnnoSolareCompare.module.scss";
import { formatItalianNumber } from "utils/formatUtils";

const RendimentiPerAnnoSolareChart = ({
  data,
  isinToName,
  performanceProducts,
}) => {
  const { t } = useTranslation();

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "0%";
    return `${formatItalianNumber(value, 1)}%`;
  };

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  // Define bar names
  const [barNames, setBarNames] = useState([]);

  // Define bar colors
  const barColors = [
    colorsConfig.paletteBarChart1,
    colorsConfig.paletteBarChart2,
    colorsConfig.paletteBarChart3,
    colorsConfig.paletteBarChart4,
    colorsConfig.paletteBarChart5,
  ];

  useEffect(() => {
    const newBarNames = [];
    for (let i = 0; i < performanceProducts.length; i++) {
      const isin = performanceProducts[i];
      const name = isinToName[isin] || `Fondo ${i + 1}`;
      newBarNames.push(name);
    }
    setBarNames(newBarNames);
  }, [performanceProducts, isinToName]);

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  const renderCustomBarLabel = (props) => {
    const { x, y, width, value } = props;
    const isPositive = Number(value) >= 0;
    const yPos = isPositive ? y - 10 : y + 20;
    return (
      <text
        x={x + width / 2}
        y={yPos}
        fill={props.fill}
        textAnchor="middle"
        fontSize={12}
        fontFamily="Arial"
      >
        {`${formatItalianNumber(value, 1)}%`}
      </text>
    );
  };

  const transformData = (rawData) => {
    return rawData.map((item) => {
      const weights = item.weights || [];

      return {
        year: item.label,
        value0: weights[0] || 0,
        value1: weights[1] || null,
        value2: weights[2] || null,
        value3: weights[3] || null,
        value4: weights[4] || null,
      };
    });
  };

  let barSize = 20;
  if (data.length > 0) {
    const getWeightsSize = data[0].weights.length;
    if (getWeightsSize < 5) {
      barSize = 20 + 5 * (5 - getWeightsSize);
    }
  }

  // Apply the transformation
  const cleanedData = data // .filter((item) => parseInt(item.label) > 2018);
  const formattedData = transformData(cleanedData);

  if (formattedData.length === 0) {
    return null;
  }

  // Calculate min and max values for proper Y-axis scaling
  const allValues = formattedData.flatMap((item) => [
    item.value0,
    item.value1,
    item.value2,
    item.value3,
    item.value4,
  ]);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  // Custom tooltip to show all values
  const CustomTooltip = (props) => {
    const { active, payload, label } = props;

    if (!active || !payload || !payload.length) return null;

    return (
      <div
        style={{
          backgroundColor: colorsConfig.finecoWhite || "#fff",
          padding: "10px",
          border: `1px solid ${colorsConfig.FinecoGrayLight1 || "#ccc"}`,
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <p
          style={{
            marginBottom: "5px",
            fontWeight: "bold",
            fontFamily: "Arial",
            fontSize: "12px",
            color: colorsConfig.finecoBlack || "#000",
          }}
        >
          {`Anno: ${label}`}
        </p>
        {payload.map((entry, index) => (
          <p
            key={`tooltip-${index}`}
            style={{
              margin: "3px 0",
              fontFamily: "Arial",
              fontSize: "12px",
              color: entry.color,
            }}
          >
            {`${entry.name}: ${formatItalianNumber(entry.value, 1)}%`}
          </p>
        ))}
      </div>
    );
  };

  const CustomizedLegend = (props) => {
    const { payload } = props;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
          paddingLeft: "18px",
          paddingRight: "18px",
          paddingTop: "10px",
          // marginBottom: 50,
        }}
      >
        {/* <div
          style={{
            color: colorsConfig.FinecoBlack || "#666",
            fontSize: 11,
            fontFamily: "Arial",
            marginTop: "0px",
            textAlign: "center",
            backgroundClip: "yellow",
            width: "100%",
          }}
        >
          {t("productCard.rendimentoAnnoSolareRightFootnote")}
        </div> */}
        <div
          style={{
            marginTop: "10px",
            transform: "translateX(-10px)",
          }}
        >
          {payload.slice().map((entry, index) => (
            <span
              key={`legend-item-${index}`}
              style={{
                margin: "0 10px 5px 10px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                  marginRight: "5px",
                }}
              ></span>
              <span
                style={{
                  color: colorsConfig.finecoBlack,
                  fontSize: 12,
                  fontFamily: "Arial",
                }}
              >
                {entry.value}
              </span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  const calcLegendH = () => {
    const chartContainer = document.getElementsByClassName(
      styles.sectionContainer,
    )[0];
    if (!chartContainer) return 50;

    const divLegend = chartContainer.getElementsByClassName(
      "recharts-legend-wrapper",
    )[0];

    let legendH = 50;
    if (divLegend) {
      const divChildren = divLegend.children[0];
      if (divChildren) {
        legendH = divChildren.clientHeight;
      }
    }

    return legendH + 0;
  };

  return (
    <div className={styles.sectionContainer}>
      <h3 className={styles.sectionTitle}>
        {t("productCard.rendimentiPerAnnoSolareTitle")}
      </h3>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={400} debounce={500}>
          <BarChart
            data={formattedData}
            margin={{ top: 25, right: 20, left: 0, bottom: 10 }}
            barGap={5}
            barCategoryGap={20}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.FinecoGrayLight1}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            {/* Reference line at 0% */}
            <ReferenceLine
              y={0}
              stroke={colorsConfig.finecoBlack}
              strokeWidth={1}
              strokeOpacity={0.8}
            />
            <XAxis
              dataKey="year"
              tick={chartCopy}
              tickLine={false}
              dy={10}
              strokeDasharray="none"
              axisLine={{ stroke: colorsConfig.FinecoGrayLight1 }}
            />
            <YAxis
              tickFormatter={formatPercent}
              tickLine={false}
              tick={chartCopy}
              domain={[
                (dataMin) => Math.floor(dataMin / 10) * 10 - 0, // Round down to nearest 10 and subtract 10 for padding
                (dataMax) => Math.ceil(dataMax / 10) * 10 + 0, // Round up to nearest 10 and add 10 for padding
              ]}
              // Generate ticks dynamically in increments of 10
              ticks={(() => {
                const minTick = Math.floor(minValue / 10) * 10 - 10;
                const maxTick = Math.ceil(maxValue / 10) * 10 + 10;
                const ticks = [];
                for (let i = minTick; i <= maxTick; i += 10) {
                  ticks.push(i);
                }
                return ticks;
              })()}
              allowDataOverflow={false}
              axisLine={{ stroke: colorsConfig.FinecoGrayLight1 }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            <Legend
              content={<CustomizedLegend />}
              align="center"
              verticalAlign="bottom"
              height={calcLegendH()} // Increased height to accommodate the centered footnote
            />

            {/* One bar for each of the 5 weight values */}
            <Bar
              dataKey="value0"
              name={barNames[0]}
              fill={barColors[0]}
              barSize={barSize}
              radius={[4, 4, 0, 0]}
              label={(props) =>
                renderCustomBarLabel({
                  ...props,
                  fill: colorsConfig.FinecoBlack,
                })
              }
              isAnimationActive={false}
            />
            {formattedData[0].value1 !== null && (
              <Bar
                dataKey="value1"
                name={barNames[1]}
                fill={barColors[1]}
                barSize={barSize}
                radius={[4, 4, 0, 0]}
                label={(props) =>
                  renderCustomBarLabel({
                    ...props,
                    fill: colorsConfig.FinecoBlack,
                  })
                }
                isAnimationActive={false}
              />
            )}
            {formattedData[0].value2 !== null && (
              <Bar
                dataKey="value2"
                name={barNames[2]}
                fill={barColors[2]}
                barSize={barSize}
                radius={[4, 4, 0, 0]}
                label={(props) =>
                  renderCustomBarLabel({
                    ...props,
                    fill: colorsConfig.FinecoBlack,
                  })
                }
                isAnimationActive={false}
              />
            )}
            {formattedData[0].value3 !== null && (
              <Bar
                dataKey="value3"
                name={barNames[3]}
                fill={barColors[3]}
                barSize={barSize}
                radius={[4, 4, 0, 0]}
                label={(props) =>
                  renderCustomBarLabel({
                    ...props,
                    fill: colorsConfig.FinecoBlack,
                  })
                }
                isAnimationActive={false}
              />
            )}
            {formattedData[0].value4 !== null && (
              <Bar
                dataKey="value4"
                name={barNames[4]}
                fill={barColors[4]}
                barSize={barSize}
                radius={[4, 4, 0, 0]}
                label={(props) =>
                  renderCustomBarLabel({
                    ...props,
                    fill: colorsConfig.FinecoBlack,
                  })
                }
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RendimentiPerAnnoSolareChart;
